import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { TrackingScope } from "@b2bportal/core-tracking";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { RoomProduct } from "@b2bportal/lodging-api";
import {
  HotelRoomCard,
  HotelsRawEventName,
  useEmitCoreHotelsRawEvent,
  useEmitCoreHotelsRawEventOnce,
  useHotelRoomSelection,
  type HotelShopListTrackingProperties,
} from "@tenant/hotels";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import HotelsShopPageHotelRoomCardSkeleton from "../HotelRoomCard/Skeleton";
import defaultStyles from "./HotelRoomSelection.module.scss";

export interface HotelRoomSelectionComponentProps
  extends PlatformComponentProps {
  goToNextStep: () => void;
}

const COMPONENT_KEY = CoreHotelsComponent.HotelRoomSelection;

export const HotelRoomSelection = (props: HotelRoomSelectionComponentProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { className, children } = props;
  const { context, handlers, text } = useHotelRoomSelection(props);

  const emitHotelShopPageRoomSelectedEvent = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelShopPageRoomSelected
  );

  const onRoomSelection = (product: RoomProduct) => {
    emitHotelShopPageRoomSelectedEvent({
      properties: {
        rate: product,
      },
    });

    handlers.onRoomSelection(product);
  };

  useEmitCoreHotelsRawEventOnce(
    {
      name: HotelsRawEventName.HotelShopPageRoomsLoaded,
    },
    !context.isLoading
  );

  const renderRoomSelectionContent = () => {
    if (context.isLoading) {
      return (
        <div className={cn("room-container")}>
          <HotelsShopPageHotelRoomCardSkeleton />
          <HotelsShopPageHotelRoomCardSkeleton />
          <HotelsShopPageHotelRoomCardSkeleton />
        </div>
      );
    }
    if (context.rooms.length === 0) {
      return (
        <div className={cn("no-rooms-container")}>
          <Icon iconName={IconNameEnum.dismiss} size="large" />
          <div className={cn("title")}>{text.noAvailableRooms.title}</div>
          <div className={cn("subtitle")}>{text.noAvailableRooms.subtitle}</div>
        </div>
      );
    }
    return (
      <div className={cn("room-container")}>
        {context.rooms.map((room, index) => (
          <TrackingScope<HotelShopListTrackingProperties>
            key={room.roomInfo.roomId}
            trackingAttributes={{ indexInList: index }}
          >
            <HotelRoomCard
              room={room}
              onRoomSelection={onRoomSelection}
              nightCount={context.nightCount}
            />
          </TrackingScope>
        ))}
      </div>
    );
  };

  return (
    <div className={clsx(block, className)}>
      {renderRoomSelectionContent()}
      {children}
    </div>
  );
};
